import React, { useState, useEffect } from "react";
import OfferForm from "./OfferForm";
import OfferTable from "./OfferTable";
import CreatableSelect from 'react-select/creatable';
import axios from "axios";
import api from "../utils/api";
import "../Admin.css";

const ProductManager = () => {
    const [editingOffer, setEditingOffer] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);
    const [showImportModal, setShowImportModal] = useState(false);
    const [importUrl, setImportUrl] = useState("");
    const [importing, setImporting] = useState(false);
    const [importError, setImportError] = useState(null);
    const [showShopeeModal, setShowShopeeModal] = useState(false);
    const [shopeeSearchTerm, setShopeeSearchTerm] = useState("");
    const [searchingShopee, setSearchingShopee] = useState(false);
    const [shopeeStatus, setShopeeStatus] = useState(null);
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState("Eletrônicos");
    const [selectedSubcategory, setSelectedSubcategory] = useState("");

    // Buscar categorias ao montar o componente
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await api.get('/categorias');
                setCategories(response.data);
            } catch (error) {
                console.error("Erro ao buscar categorias:", error);
            }
        };
        fetchCategories();
    }, []);

    // Atualizar subcategorias quando a categoria muda
    useEffect(() => {
        if (selectedCategory) {
            const cat = categories.find(c => c.name === selectedCategory);
            setSubcategories(cat?.subcategories || []);
        } else {
            setSubcategories([]);
        }
    }, [selectedCategory, categories]);

    const handleEditClick = (offer) => {
        setEditingOffer(offer);
    };

    const handleSuccess = () => {
        setRefreshKey((prevKey) => prevKey + 1);
        setEditingOffer(null);
    };

    const handleCancelEdit = () => {
        setEditingOffer(null);
    };

    const handleShopeeSearch = async () => {
        if (!shopeeSearchTerm.trim()) {
            setShopeeStatus({ type: 'error', msg: "Por favor, digite um produto ou link." });
            return;
        }

        setSearchingShopee(true);
        setShopeeStatus({ type: 'info', msg: "Iniciando busca na Shopee via API Oficial..." });

        try {
            // Agora chama o BACKEND diretamente (que gerencia a assinatura e a API da Shopee)
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/shopee/sync`, {
                keyword: shopeeSearchTerm,
                categoria: selectedCategory,
                subcategoria: selectedSubcategory
            });

            if (response.data.success) {
                const novos = response.data.novosCadastros;
                setShopeeStatus({ 
                    type: 'success', 
                    msg: novos > 0 
                        ? `${novos} novos produtos cadastrados com sucesso!` 
                        : "Nenhum produto novo encontrado (podem já estar cadastrados)." 
                });
                
                setTimeout(() => {
                    setShowShopeeModal(false);
                    setShopeeSearchTerm("");
                    setShopeeStatus(null);
                    setRefreshKey(prev => prev + 1); // Atualiza a lista na tela
                }, 3000);
            }
        } catch (error) {
            console.error("Erro ao buscar na Shopee:", error);
            setShopeeStatus({ 
                type: 'error', 
                msg: error.response?.data?.message || "Erro ao conectar com o serviço de busca." 
            });
        } finally {
            setSearchingShopee(false);
        }
    };

    const handleImport = async () => {
        if (!importUrl.trim()) {
            setImportError("Por favor, insira uma URL válida.");
            return;
        }

        setImporting(true);
        setImportError(null);

        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/affiliate/import`, {
                url: importUrl
            });

            if (response.data.success) {
                const importedData = response.data.data;
                const newOffer = {
                    titulo: importedData.titulo,
                    descricao: importedData.descricao,
                    preco_original: "",
                    preco_desconto: "",
                    imagem_url: importedData.imagem_url,
                    loja: importedData.loja,
                    link_afiliado: importedData.link_afiliado,
                    status: "ATIVA",
                    categoria: "Eletrônicos",
                    subcategoria: ""
                };
                setEditingOffer(newOffer);
                setShowImportModal(false);
                setImportUrl("");
            }
        } catch (error) {
            console.error("Erro ao importar:", error);
            setImportError(error.response?.data?.error || "Erro ao importar oferta.");
        } finally {
            setImporting(false);
        }
    };

    return (
        <>
            <div className="page-header">
                <h2>Gerenciamento de Produtos</h2>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <button className="btn-secondary" style={{ backgroundColor: '#ff4742', color: 'white', border: 'none' }} onClick={() => setShowShopeeModal(true)}>
                        <i className="fas fa-shopping-bag"></i> Buscar na Shopee
                    </button>
                    <button className="btn-secondary" onClick={() => setShowImportModal(true)}>
                        <i className="fas fa-download"></i> Importar de Link
                    </button>
                    <button className="btn-primary" onClick={() => setEditingOffer({})}>
                        <i className="fas fa-plus"></i> Nova Oferta
                    </button>
                </div>
            </div>

            {/* Shopee Search Modal */}
            {showShopeeModal && (
                <div className="modal-overlay" onClick={() => setShowShopeeModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Buscar Produtos na Shopee</h3>
                            <button className="modal-close" onClick={() => setShowShopeeModal(false)}>×</button>
                        </div>
                        <div className="modal-body">
                            <p style={{ marginBottom: '15px', color: '#666' }}>
                                Digite o nome do produto ou link. O n8n irá buscar e cadastrar automaticamente.
                            </p>
                            <div className="form-group">
                                <label>Produto ou Link Shopee</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    placeholder="Ex: Xiaomi Poco X6 ou link do produto"
                                    value={shopeeSearchTerm}
                                    onChange={(e) => setShopeeSearchTerm(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && handleShopeeSearch()}
                                />
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginTop: '15px' }}>
                                <div className="form-group">
                                    <label>Categoria Principal</label>
                                    <CreatableSelect
                                        options={categories.map(c => ({ value: c.name, label: c.name }))}
                                        value={selectedCategory ? { value: selectedCategory, label: selectedCategory } : null}
                                        onChange={(opt) => {
                                            setSelectedCategory(opt?.value || "");
                                            setSelectedSubcategory("");
                                        }}
                                        placeholder="Selecione..."
                                        isClearable
                                        formatCreateLabel={(v) => `Criar "${v}"`}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Subcategoria</label>
                                    <CreatableSelect
                                        options={subcategories.map(s => ({ value: s.name, label: s.name }))}
                                        value={selectedSubcategory ? { value: selectedSubcategory, label: selectedSubcategory } : null}
                                        onChange={(opt) => setSelectedSubcategory(opt?.value || "")}
                                        placeholder="Opcional..."
                                        isClearable
                                        isDisabled={!selectedCategory}
                                        formatCreateLabel={(v) => `Criar "${v}"`}
                                    />
                                </div>
                            </div>
                            {shopeeStatus && (
                                <div className={`alert alert-${shopeeStatus.type === 'error' ? 'danger' : shopeeStatus.type === 'info' ? 'info' : 'success'}`}>
                                    {shopeeStatus.msg}
                                </div>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button className="btn-secondary" onClick={() => setShowShopeeModal(false)}>Cancelar</button>
                            <button className="btn-primary" onClick={handleShopeeSearch} disabled={searchingShopee}>
                                {searchingShopee ? 'Buscando...' : 'Iniciar Busca'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Import Modal */}
            {showImportModal && (
                <div className="modal-overlay" onClick={() => setShowImportModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Importar Oferta de Link</h3>
                            <button className="modal-close" onClick={() => setShowImportModal(false)}>×</button>
                        </div>
                        <div className="modal-body">
                            <p style={{ marginBottom: '15px', color: '#666' }}>
                                Cole o link do produto abaixo. Vamos importar título, descrição e imagem.
                            </p>
                            <div className="form-group">
                                <label>URL do Produto</label>
                                <input
                                    type="url"
                                    className="form-input"
                                    placeholder="https://www.amazon.com.br/..."
                                    value={importUrl}
                                    onChange={(e) => setImportUrl(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && handleImport()}
                                />
                            </div>
                            {importError && <div className="alert alert-danger">{importError}</div>}
                        </div>
                        <div className="modal-footer">
                            <button className="btn-secondary" onClick={() => setShowImportModal(false)}>Cancelar</button>
                            <button className="btn-primary" onClick={handleImport} disabled={importing}>
                                {importing ? 'Importando...' : 'Importar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Offer Edit Modal */}
            {editingOffer && (
                <div className="modal-overlay" onClick={handleCancelEdit}>
                    <div className="modal-content modal-lg" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px' }}>
                        <div className="modal-header">
                            <h3>{editingOffer.id ? 'Editar Oferta' : 'Nova Oferta'}</h3>
                            <button className="modal-close" onClick={handleCancelEdit}>×</button>
                        </div>
                        <div className="modal-body">
                            <OfferForm
                                editingOffer={editingOffer.id ? editingOffer : null}
                                importedData={!editingOffer.id ? editingOffer : null}
                                onSuccess={handleSuccess}
                                onCancel={handleCancelEdit}
                            />
                        </div>
                    </div>
                </div>
            )}

            <div className="card-container">
                <div className="card-header">
                    <h3>Lista de Produtos</h3>
                </div>
                <OfferTable onEditClick={handleEditClick} refreshKey={refreshKey} />
            </div>
        </>
    );
};

export default ProductManager;
